import React, { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Briefcase,
  Banknote,
  Users,
  Mail,
  Paperclip,
  Send,
  Ambulance,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Overline, Reveal } from "../components/common";

// Role content lives in the locale files (en.json / fr.json) under each role's
// locKey. Set hidden: true to keep a listing's data without displaying it.
const ROLES = [
  {
    id: "outreach-specialist",
    hidden: true, // BC listing hidden for now — remove this flag to re-enable
    positions: 2,
    locKey: "role",
    titleKey: "role.titleOutreach",
  },
  {
    id: "community-engagement",
    hidden: true, // hidden for now — remove this flag to re-enable
    positions: 1,
    locKey: "roleCES",
    titleKey: "roleCES.title",
  },
  {
    id: "outreach-on",
    positions: 1,
    locKey: "roleOutreachON",
    titleKey: "roleOutreachON.titleOutreach",
  },
  {
    id: "outreach-marketing-coordinator",
    positions: 1,
    locKey: "roleOutreachMarketing",
    titleKey: "roleOutreachMarketing.title",
  },
  {
    id: "driver",
    positions: 1,
    locKey: "roleDriver",
    titleKey: "roleDriver.title",
  },
];

const VISIBLE_ROLES = ROLES.filter((r) => !r.hidden);

const APPLY_EMAIL = "info@hepcure.ca";

// Renders **bold** markers inside locale bullet strings
const renderBold = (s) =>
  String(s)
    .split("**")
    .map((part, i) => (i % 2 ? <b key={i}>{part}</b> : part));

const RoleMeta = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-slate-600">
    <Icon className="h-4 w-4 text-brand-teal" strokeWidth={1.8} />
    <span>{children}</span>
  </div>
);

const ACCEPT = ".pdf,.doc,.docx";
const ALLOWED_EXTS = [".pdf", ".doc", ".docx"];
const MAX_BYTES = 5 * 1024 * 1024;

// Formats a North American phone number as the user types: (123) 456-7890
const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const scrollToForm = () => {
  document
    .getElementById("apply-form")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const ApplicationForm = () => {
  const { t } = useTranslation();
  const defaultRole = t(VISIBLE_ROLES[0].titleKey);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: defaultRole,
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) {
      setFile(null);
      return;
    }
    const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      toast.error(t("careersForm.unsupportedTitle"), {
        description: t("careersForm.unsupportedBody"),
      });
      e.target.value = "";
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error(t("careersForm.tooLargeTitle"), {
        description: t("careersForm.tooLargeBody"),
      });
      e.target.value = "";
      setFile(null);
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error(t("careersForm.resumeRequiredTitle"), {
        description: t("careersForm.resumeRequiredBody"),
      });
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("first_name", form.firstName);
      data.append("last_name", form.lastName);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("role", form.role);
      data.append("resume", file);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/careers/apply`,
        { method: "POST", body: data }
      );
      if (!res.ok) throw new Error("Request failed");
      toast.success(t("careersForm.successTitle"), {
        description: t("careersForm.successBody"),
      });
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        role: defaultRole,
      });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(t("careersForm.errorTitle"), {
        description: t("careersForm.errorBody"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="careers-form"
      className="rounded-3xl border border-gray-100 bg-brand-offwhite p-6 sm:p-9 shadow-sm"
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="firstName">{t("careersForm.firstName")}</Label>
          <Input
            id="firstName"
            data-testid="careers-first-name"
            required
            value={form.firstName}
            onChange={update("firstName")}
            className="mt-1.5 bg-white"
          />
        </div>
        <div>
          <Label htmlFor="lastName">{t("careersForm.lastName")}</Label>
          <Input
            id="lastName"
            data-testid="careers-last-name"
            required
            value={form.lastName}
            onChange={update("lastName")}
            className="mt-1.5 bg-white"
          />
        </div>
        <div>
          <Label htmlFor="applicantEmail">{t("careersForm.email")}</Label>
          <Input
            id="applicantEmail"
            type="email"
            data-testid="careers-email"
            required
            value={form.email}
            onChange={update("email")}
            className="mt-1.5 bg-white"
          />
        </div>
        <div>
          <Label htmlFor="applicantPhone">{t("careersForm.phone")}</Label>
          <Input
            id="applicantPhone"
            type="tel"
            data-testid="careers-phone"
            value={form.phone}
            onChange={(e) =>
              setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))
            }
            className="mt-1.5 bg-white"
          />
        </div>
      </div>

      <div className="mt-5">
        <Label htmlFor="role">{t("careersForm.position")}</Label>
        <select
          id="role"
          data-testid="careers-role"
          value={form.role}
          onChange={update("role")}
          className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal"
        >
          {VISIBLE_ROLES.map((r) => (
            <option key={r.id} value={t(r.titleKey)}>
              {t(r.titleKey)}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <Label htmlFor="resume">{t("careersForm.resumeLabel")}</Label>
        <label
          htmlFor="resume"
          className="mt-1.5 flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500 transition-colors hover:border-brand-teal"
        >
          <Paperclip className="h-4 w-4 text-brand-teal" />
          <span data-testid="careers-resume-name" className="truncate">
            {file ? file.name : t("careersForm.resumePlaceholder")}
          </span>
        </label>
        <input
          id="resume"
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          onChange={onFile}
          data-testid="careers-resume"
          className="sr-only"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="careers-submit"
        className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors disabled:opacity-60"
      >
        <Send className="h-4 w-4" />
        {submitting ? t("careersForm.submitting") : t("careersForm.submit")}
      </button>

      <p className="mt-4 text-center text-xs text-slate-400">
        {t("careersForm.preferEmail")}{" "}
        <span className="font-medium text-brand-teal">{APPLY_EMAIL}</span>.
      </p>
    </form>
  );
};

export default function Careers() {
  const { t, i18n } = useTranslation();
  return (
    <div data-testid="careers-page">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 bg-gradient-to-b from-brand-mist/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t("careers.hero.overline")}</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05] max-w-4xl">
              {t("careers.hero.title")}
            </h1>
            <p className="mt-6 max-w-2xl lg:max-w-4xl text-lg text-slate-500 leading-relaxed">
              {t("careers.hero.bodyPart1")}{" "}
              <span className="font-semibold text-brand-navy">
                {t("careers.hero.bodyPart2")}
              </span>{" "}
              {t("careers.hero.bodyPart3")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Roles */}
      <section className="py-10 sm:py-14 bg-white" data-testid="careers-roles">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {VISIBLE_ROLES.map((role) => {
            // Localized sections keep the same order as the secKeys list.
            const lk = role.locKey;
            const rt = (k) => t(`${lk}.${k}`);
            const has = (k) => i18n.exists(`${lk}.${k}`);
            const secKeys = ["schedule", "responsibilities", "qualifications", "compensationBlock", "extras"];
            const locSections = secKeys
              .map((k) => t(`${lk}.sections.${k}`, { returnObjects: true }))
              .filter((s) => s && s.heading);
            return (
            <Reveal key={role.id}>
              <div
                data-testid={`role-${role.id}`}
                className="rounded-3xl border border-gray-100 bg-brand-offwhite p-6 sm:p-9 shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold font-heading text-brand-navy">
                      {t(role.titleKey)}
                    </h2>
                    <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand-teal/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-teal">
                      <Users className="h-3.5 w-3.5" strokeWidth={2} />
                      {role.positions}{" "}
                      {role.positions === 1
                        ? t("careers.role.positionSuffixOne")
                        : t("careers.role.positionsSuffix")}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={scrollToForm}
                    data-testid={`role-apply-${role.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-6 py-3 text-sm font-semibold transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    {t("careers.role.apply")}
                  </button>
                </div>

                <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <Ambulance
                      className="h-4 w-4 shrink-0 text-brand-teal mt-0.5"
                      strokeWidth={1.8}
                    />
                    <span>
                      {rt("locationLabel")}
                      <span className="block text-slate-500">
                        {rt("location")}
                        {has("locationBc") && (
                          <span className="block">{rt("locationBc")}</span>
                        )}
                      </span>
                    </span>
                  </div>
                  <RoleMeta icon={Briefcase}>{rt("type")}</RoleMeta>
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <Banknote
                      className="h-4 w-4 shrink-0 text-brand-teal mt-0.5"
                      strokeWidth={1.8}
                    />
                    <span>
                      {rt("compensation")}
                      {has("compensationBased") && (
                        <span className="block text-slate-500">
                          {rt("compensationBased")}
                        </span>
                      )}
                      {has("compensationSub1") && (
                        <span className="block text-slate-500">
                          {rt("compensationSub1")}
                        </span>
                      )}
                      {has("compensationSub2") && (
                        <span className="block text-slate-500">
                          {rt("compensationSub2")}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <p className="mt-6 text-slate-600 leading-relaxed">
                  {rt("summary")}
                </p>

                <Accordion type="single" collapsible className="mt-4 w-full">
                  <AccordionItem value="details" className="border-gray-200">
                    <AccordionTrigger
                      data-testid={`role-details-${role.id}`}
                      className="text-sm font-semibold text-brand-teal hover:no-underline"
                    >
                      {rt("viewFullDetails")}
                    </AccordionTrigger>
                    <AccordionContent className="pb-2">
                      <div className="space-y-7 pt-2">
                        {locSections.map((s, si) => (
                          <div key={si}>
                            <h3 className="text-base font-semibold text-brand-navy">
                              {s.heading}
                            </h3>
                            <ul className="mt-3 space-y-2.5">
                              {(s.bullets || []).map((b, i) => (
                                <li
                                  key={i}
                                  className="flex gap-2.5 text-sm text-slate-600 leading-relaxed"
                                >
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-teal" />
                                  <span>{renderBold(b)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </Reveal>
            );
          })}

          {/* Apply */}
          <Reveal>
            <div id="apply-form" className="scroll-mt-28">
              <h2 className="text-2xl font-bold font-heading text-brand-navy text-center">
                {t("role.applyNowTitle")}
              </h2>
              <p className="mt-3 mb-6 text-center text-slate-500 leading-relaxed max-w-xl mx-auto">
                {t("role.applyBody")}
              </p>
              <ApplicationForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
